import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Download, ArrowRight } from 'lucide-react';
import { useInvoice } from '../../../../hooks/useInvoice';

export const PaymentSuccessModal = ({
  isOpen,
  onClose,
  onGoToBillingHistory,
  paymentData = {},
  companyId,
}) => {
  const { triggerInvoiceDownload, isDownloading, downloadSuccess, downloadUrl, downloadInvoicePdf, resetDownload } = useInvoice();
  const [downloadError, setDownloadError] = useState(null);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const hasDownloadedRef = useRef(false);

  useEffect(() => {
    if (downloadSuccess && downloadUrl && !hasDownloadedRef.current && isOpen) {
      hasDownloadedRef.current = true;
      setIsDownloadingInvoice(false);
      downloadInvoicePdf();
      setTimeout(() => {
        onClose();
        onGoToBillingHistory?.();
      }, 500);
    }
  }, [downloadSuccess, downloadUrl, downloadInvoicePdf, onClose, onGoToBillingHistory, isOpen]);

  useEffect(() => {
    if (isDownloading === false && isDownloadingInvoice && downloadError) {
      setIsDownloadingInvoice(false);
    }
  }, [isDownloading, downloadError]);

  useEffect(() => {
    if (!isOpen) {
      hasDownloadedRef.current = false;
      setDownloadError(null);
      setIsDownloadingInvoice(false);
      resetDownload();
    }
  }, [isOpen, resetDownload]);

  const {
    invoiceNumber = 'INV-2025-001',
    datePaid = new Date().toLocaleDateString('en-GB'),
    planName = 'Yearly Plan',
    totalAmount = '0',
    invoiceId = null,
  } = paymentData || {};

  const handleClose = () => {
    onClose();
    onGoToBillingHistory?.();
  };

  const handleDownloadInvoice = async () => {
    if (!invoiceId || !companyId) {
      setDownloadError('Unable to download invoice. Missing invoice or company information.');
      return;
    }

    try {
      setDownloadError(null);
      setIsDownloadingInvoice(true);
      await triggerInvoiceDownload({ companyId, invoiceId });
    } catch (error) {
      setDownloadError('Failed to download invoice. Please try again.');
      setIsDownloadingInvoice(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-20"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Success Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-white rounded-full opacity-20 scale-150 animate-pulse"></div>
              <CheckCircle className="h-16 w-16 text-white relative z-10" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
          <p className="text-blue-100">Your subscription is now active</p>
        </div>

        {/* Receipt Details */}
        <div className="px-6 py-8 space-y-6">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">Receipt Details</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Invoice Number:</span>
                <span className="font-medium text-gray-900 dark:text-white">{invoiceNumber}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Date Paid:</span>
                <span className="font-medium text-gray-900 dark:text-white">{datePaid}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Plan:</span>
                <span className="font-medium text-gray-900 dark:text-white">{planName}</span>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-900 dark:text-white">Total Paid:</span>
                  <span className="font-bold text-[hsl(158,48%,32%)] dark:text-[hsl(158,45%,55%)] text-lg">₹{totalAmount}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleClose}
              className="w-full bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-medium py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              Go to Billing History
            </button>
            
            <button
              type="button"
              onClick={handleDownloadInvoice}
              disabled={isDownloadingInvoice || !invoiceId}
              className={`w-full border-2 border-[hsl(158,48%,36%)] font-medium py-3 rounded-lg transition flex items-center justify-center gap-2 ${
                isDownloadingInvoice || !invoiceId
                  ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                  : 'text-[hsl(158,48%,32%)] hover:bg-[hsl(158,48%,36%)]/10 dark:hover:bg-slate-800'
              }`}
            >
              <Download className={`h-4 w-4 ${isDownloadingInvoice ? 'animate-spin' : ''}`} />
              {isDownloadingInvoice ? 'Downloading...' : 'Download Invoice'}
            </button>

            {downloadError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-center text-red-600 dark:text-red-400 text-sm">
                {downloadError}
              </div>
            )}
          </div>

          {/* What's Next Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">What's next?</h4>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 font-bold mt-0.5">•</span>
                <span>Set up your WhatsApp Business API profile</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 font-bold mt-0.5">•</span>
                <span>Import your contacts</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 dark:text-blue-400 font-bold mt-0.5">•</span>
                <span>Create your first messaging campaign</span>
              </li>
            </ul>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 text-center text-xs text-gray-500 dark:text-gray-400">
            <p>If you have any questions about this receipt, simply reply to this email or reach out to our support team at <a href="mailto:support@prodyt.com" className="text-blue-600 hover:underline">support@prodyt.com</a></p>
            <p className="mt-2">© 2026 Prodyt Inc. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
